// @ts-nocheck
// components/seo/CourseCategoryPage.tsx — Editorial course-category template
// Used by every /courses/{slug} page (programming, ai, cyber, ui-ux, etc.)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft, AlertTriangle } from 'lucide-react-native';

import { theme, ACCENT, CRIMSON, FOREST, INK, INK_FAINT, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, Title,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { trackButtonClick, trackFunnelStep, trackPageView } from '@/utils/analytics';
import { usePageEngagement } from '@/hooks/usePageEngagement';

export type FAQItem = { q: string; a: string };
export type Section = { id: string; title: string; body: string; bullets?: string[] };

export type CategoryPageConfig = {
  title: string;
  metaTitle?: string;
  subtitle: string;
  slug?: string;
  metaDescription?: string;
  chips?: string[];
  chatSeed: string;
  sections: Section[];
  checklist: string[];
  redFlags: string[];
  faq: FAQItem[];
};

const SITE_URL = 'https://kursiaii.com';
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

export function CourseCategoryPage({ config }: { config: CategoryPageConfig }) {
  const router = useRouter();

  const normalizedSlug = (config.slug ?? '').trim();
  const pageTitle = config.metaTitle ?? config.title;
  const pageDescription = config.metaDescription ?? config.subtitle;
  const pageUrl = normalizedSlug ? `${SITE_URL}/courses/${normalizedSlug}` : `${SITE_URL}/courses`;

  const scrollRef = useRef<ScrollView | null>(null);
  const [anchors, setAnchors] = useState<Record<string, number>>({});

  usePageEngagement(normalizedSlug ? `/courses/${normalizedSlug}` : '/courses');

  useEffect(() => {
    if (!normalizedSlug) return;
    void trackPageView(`/courses/${normalizedSlug}`);
    void trackFunnelStep('course_view', { slug: normalizedSlug });
  }, [normalizedSlug]);

  const courseSchema = JSON.stringify({
    '@context': 'https://schema.org', '@type': 'Course',
    name: config.title, description: pageDescription, url: pageUrl, inLanguage: 'he',
    provider: { '@type': 'Organization', name: 'Kursi', url: SITE_URL },
    teaches: config.chips ?? [],
    courseMode: ['online', 'onsite'],
    educationalLevel: 'Beginner to Advanced',
  });
  const faqSchema = config.faq.length > 0 ? JSON.stringify({
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: config.faq.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  }) : null;
  const breadcrumbSchema = JSON.stringify({
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
      { '@type': 'ListItem', position: 3, name: config.title, item: pageUrl },
    ],
  });
  const webPageSchema = JSON.stringify({
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: pageTitle, description: pageDescription, url: pageUrl, inLanguage: 'he',
  });

  const setAnchor = useCallback((id: string, y: number) => {
    setAnchors(prev => (prev[id] === y ? prev : { ...prev, [id]: y }));
  }, []);
  const scrollTo = useCallback((id: string) => {
    const y = anchors[id];
    if (typeof y !== 'number') return;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: true });
  }, [anchors]);

  const goToChat = useCallback((source: 'hero_cta' | 'bottom_cta') => {
    void trackButtonClick(source === 'hero_cta' ? 'course_cta_chat' : 'course_bottom_cta', `/courses/${config.slug ?? ''}`, { slug: config.slug });
    void trackFunnelStep('course_cta_click', { slug: config.slug, source });
    router.push({ pathname: '/chat', params: { initialMessage: config.chatSeed, skipWelcome: 'true' } });
  }, [router, config.chatSeed, config.slug]);

  const toc = useMemo(() => ([
    ...config.sections.map(s => ({ id: s.id, label: s.title })),
    { id: 'checklist', label: 'צ׳ק־ליסט לפני תשלום' },
    { id: 'redflags', label: 'דגלים אדומים' },
    { id: 'faq', label: 'שאלות נפוצות' },
  ]), [config.sections]);

  return (
    <Paper>
      <AccessibilityButton />
      <Stack.Screen options={{ title: pageTitle }} />
      {IS_WEB && (
        <Head>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={pageUrl} />
          <link rel="alternate" hrefLang="he" href={pageUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:image" content={OG_IMAGE} />
          <meta name="twitter:card" content="summary_large_image" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: courseSchema }} />
          {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: webPageSchema }} />
        </Head>
      )}

      <ScrollView ref={r => (scrollRef.current = r)} style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1100}>
          <PageHero
            kicker={`גיליון ${config.chatSeed} · עיתון Kursi`}
            title={<>{config.title}</>}
            lede={config.subtitle}
            meta={`קריאה: ${Math.max(4, config.sections.length * 2)} דק׳ · עודכן השבוע`}
          />

          {/* Quick-answer box (TL;DR) */}
          <View style={s.answerBox}>
            <Text style={s.answerLabel}>תשובה מהירה · בקצרה</Text>
            <View style={{ height: 14 }} />
            <View style={s.answerRow}><Text style={s.answerKey}>מה זה</Text><Text style={s.answerVal}>{config.subtitle}</Text></View>
            <View style={s.answerRow}><Text style={s.answerKey}>למי</Text><Text style={s.answerVal}>{config.chips?.slice(0, 3).join(' · ') || 'למי שמחפש בחירת קורס פרקטית עם תוצרים'}</Text></View>
            <View style={s.answerRow}><Text style={s.answerKey}>איך</Text><Text style={s.answerVal}>צ׳ק־ליסט, דגלים אדומים, ואז התאמה אישית בצ׳אט.</Text></View>
          </View>

          {!!config.chips?.length && (
            <View style={s.chipsRow}>
              {config.chips.map((c) => (
                <View key={c} style={s.chip}>
                  <Text style={s.chipText}>{c}</Text>
                </View>
              ))}
            </View>
          )}

          <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 14, marginTop: 28 } as any}>
            <EditorialButton label="התאמה בצ׳אט · 3 התאמות" onPress={() => goToChat('hero_cta')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
            <EditorialButton label="חזרה לכל הקטגוריות" onPress={() => router.push('/courses')} variant="secondary" size="lg" />
          </View>
        </Section>

        {/* TOC */}
        <Section py="md" px="md" max={1100}>
          <View onLayout={(e) => setAnchor('toc', e.nativeEvent.layout.y)} style={s.tocBlock}>
            <Eyebrow>תוכן הגיליון</Eyebrow>
            <View style={{ height: 14 }} />
            <View style={s.tocGrid}>
              {toc.map((t, i) => (
                <Pressable
                  key={t.id}
                  onPress={() => { void trackButtonClick('course_toc_click', `/courses/${config.slug ?? ''}`, { section_id: t.id }); scrollTo(t.id); }}
                  style={({ hovered }: any) => [s.tocItem, hovered && { backgroundColor: INK }]}
                  accessibilityRole="button"
                >
                  <Text style={s.tocIdx}>{String(i + 1).padStart(2, '0')}</Text>
                  <Text style={s.tocText}>{t.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </Section>

        {/* SECTIONS */}
        <Section py="lg" px="md" max={1100}>
          {config.sections.map((sec, i) => (
            <View key={sec.id} onLayout={(e) => setAnchor(sec.id, e.nativeEvent.layout.y)} style={s.contentSection}>
              <View style={s.contentHead}>
                <Numeral size={88}>{String(i + 1).padStart(2, '0')}</Numeral>
                <View style={{ flex: 1, paddingRight: 24 }}>
                  <Title level={2}>{sec.title}</Title>
                  <View style={{ height: 12 }} />
                  <Text style={s.body}>{sec.body}</Text>
                </View>
              </View>
              {!!sec.bullets?.length && (
                <View style={s.bulletsBlock}>
                  {sec.bullets.map((b, idx) => (
                    <View key={`${sec.id}-${idx}`} style={s.bulletRow}>
                      <Text style={s.bulletMark}>—</Text>
                      <Text style={s.bulletText}>{b}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          ))}
        </Section>

        {/* CHECKLIST + RED FLAGS — two-column block */}
        <Section py="md" px="md" max={1100}>
          <View style={s.cardsRow}>
            <View
              onLayout={(e) => setAnchor('checklist', e.nativeEvent.layout.y)}
              style={[s.cardSide, s.cardCheck]}
            >
              <Eyebrow color={FOREST}>צ׳ק־ליסט לפני תשלום</Eyebrow>
              <View style={{ height: 14 }} />
              <Title level={3} color={INK}>מה לוודא לפני שאתם מוציאים שקל</Title>
              <View style={{ height: 18 }} />
              {config.checklist.map((x, i) => (
                <View key={`c-${i}`} style={s.checkRow}>
                  <Text style={[s.bulletMark, { color: FOREST }]}>✓</Text>
                  <Text style={s.bulletText}>{x}</Text>
                </View>
              ))}
            </View>

            <View
              onLayout={(e) => setAnchor('redflags', e.nativeEvent.layout.y)}
              style={[s.cardSide, s.cardRed]}
            >
              <Eyebrow color={CRIMSON}>דגלים אדומים</Eyebrow>
              <View style={{ height: 14 }} />
              <Title level={3} color={INK}>אם תראו את אלה — תברחו</Title>
              <View style={{ height: 18 }} />
              {config.redFlags.map((x, i) => (
                <View key={`r-${i}`} style={s.checkRow}>
                  <View style={{ marginTop: 4 }}><AlertTriangle size={14} color={CRIMSON} /></View>
                  <Text style={[s.bulletText, { color: INK }]}>{x}</Text>
                </View>
              ))}
            </View>
          </View>
        </Section>

        {/* FAQ */}
        <Section py="xxl" px="md" max={1000}>
          <View onLayout={(e) => setAnchor('faq', e.nativeEvent.layout.y)}>
            <Eyebrow>שאלות שמגיעות אלינו</Eyebrow>
            <View style={{ height: 14 }} />
            <Headline level={2}>שאלות נפוצות בתחום הזה.</Headline>
            <View style={{ height: 28 }} />
            <View style={{ borderTopWidth: 1, borderTopColor: INK }}>
              {config.faq.map((f, i) => (
                <View key={`f-${i}`} style={s.faqRow}>
                  <Text style={s.faqIdx}>{String(i + 1).padStart(2, '0')}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.faqQ}>{f.q}</Text>
                    <View style={{ height: 8 }} />
                    <Text style={s.faqA}>{f.a}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </Section>

        {/* FINAL CTA */}
        <Section py="xxl" px="md" max={1100}>
          <View style={s.finalCta}>
            <Headline level={2} color={PAPER}>מוכנים לבחור?</Headline>
            <View style={{ height: 12 }} />
            <Text style={[s.body, { color: PAPER_SOFT, maxWidth: 540 }]}>שורת חיפוש אחת. שלוש התאמות מדויקות. בלי טפסים, בלי התחייבות.</Text>
            <View style={{ height: 24 }} />
            <EditorialButton label="התחל התאמה בצ׳אט" onPress={() => goToChat('bottom_cta')} variant="accent" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
          </View>
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'כל הקטגוריות', href: '/courses' },
              { label: 'תחומי לימוד', href: '/fields' },
              { label: 'מאמרים', href: '/articles' },
              { label: 'המתודולוגיה', href: '/methodology' },
            ]},
            { title: 'המוצר', items: [
              { label: 'איך זה עובד', href: '/how-it-works' },
              { label: 'שאלון התאמה', href: '/quiz' },
              { label: 'שאלות נפוצות', href: '/faq' },
              { label: 'אודות', href: '/about' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  answerBox: {
    marginTop: 28,
    borderWidth: 1, borderColor: INK,
    padding: 22, backgroundColor: PAPER_SOFT,
  },
  answerLabel: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right' },
  answerRow: { flexDirection: 'row-reverse', gap: 14, paddingVertical: 8, borderTopWidth: 1, borderTopColor: 'rgba(26,22,17,0.10)' } as any,
  answerKey: { fontFamily: MONO, fontSize: 11, color: INK_MUTED, letterSpacing: 1.4, textTransform: 'uppercase' as any, width: 48, textAlign: 'right' },
  answerVal: { flex: 1, fontFamily: SANS, fontSize: 15, color: INK, lineHeight: 24, textAlign: 'right' },

  chipsRow: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8, marginTop: 18 } as any,
  chip: { paddingHorizontal: 12, paddingVertical: 5, borderWidth: 1, borderColor: INK, backgroundColor: 'transparent' },
  chipText: { fontFamily: MONO, fontSize: 11, color: INK, letterSpacing: 1.2, textTransform: 'uppercase' as any },

  tocBlock: { borderTopWidth: 2, borderTopColor: INK, paddingTop: 24, paddingBottom: 8 },
  tocGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  tocItem: {
    width: IS_WEB ? '50%' : '100%',
    paddingVertical: 14, paddingHorizontal: 18,
    flexDirection: 'row-reverse', alignItems: 'baseline', gap: 14,
    borderBottomWidth: 1, borderBottomColor: INK,
    ...(IS_WEB ? { transition: 'background 180ms ease' } as any : {}),
  } as any,
  tocIdx: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.4 },
  tocText: { flex: 1, fontFamily: SANS_MED, fontSize: 15, color: INK, textAlign: 'right' },

  contentSection: { paddingVertical: 32, borderTopWidth: 1, borderTopColor: INK },
  contentHead: { flexDirection: 'row-reverse', alignItems: 'flex-start' } as any,
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT, textAlign: 'right' },

  bulletsBlock: { paddingRight: 112, marginTop: 14 },
  bulletRow: { flexDirection: 'row-reverse', gap: 12, paddingVertical: 4 } as any,
  bulletMark: { fontFamily: SERIF_BOLD, fontSize: 18, color: ACCENT, lineHeight: 28 },
  bulletText: { flex: 1, fontFamily: SANS, fontSize: 16, lineHeight: 28, color: INK, textAlign: 'right' },

  cardsRow: { flexDirection: IS_WEB ? 'row-reverse' : 'column', gap: 0 } as any,
  cardSide: { flex: 1, padding: 28, borderWidth: 1, borderColor: INK, marginLeft: -1 },
  cardCheck: { backgroundColor: PAPER_SOFT },
  cardRed: { backgroundColor: PAPER_SOFT },
  checkRow: { flexDirection: 'row-reverse', gap: 12, paddingVertical: 6 } as any,

  faqRow: { flexDirection: 'row-reverse', gap: 18, paddingVertical: 22, borderBottomWidth: 1, borderBottomColor: INK } as any,
  faqIdx: { fontFamily: MONO, fontSize: 13, color: ACCENT, letterSpacing: 1.4, width: 32 },
  faqQ: { fontFamily: SERIF_BOLD, fontSize: 20, color: INK, letterSpacing: -0.3, textAlign: 'right', lineHeight: 28 },
  faqA: { fontFamily: SANS, fontSize: 16, lineHeight: 26, color: INK_SOFT, textAlign: 'right' },

  finalCta: { backgroundColor: INK, padding: 48, alignItems: 'flex-start' },
});
